import { useState, useRef, useCallback, useEffect } from 'react';

// The torch constraint is a W3C extension for camera flash control, not yet in TypeScript's lib.dom.d.ts.
// Augment the built-in interfaces so applyConstraints/getCapabilities work without casts.
declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: ConstrainBoolean;
  }
}

interface CameraConfig {
  facingMode?: 'user' | 'environment';
  width?: { ideal: number };
  height?: { ideal: number };
}

interface UseCameraReturn {
  stream: MediaStream | null;
  error: string | null;
  isInitializing: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  capturePhoto: () => string | null;
  clearError: () => void;
  flashEnabled: boolean;
  hasFlashSupport: boolean;
  toggleFlash: () => Promise<void>;
  facingMode: 'user' | 'environment';
  hasMultipleCameras: boolean;
  switchCamera: () => Promise<void>;
}

export const useCamera = (config: CameraConfig = {}): UseCameraReturn => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [hasFlashSupport, setHasFlashSupport] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(config.facingMode || 'environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const facingModeRef = useRef<'user' | 'environment'>(config.facingMode || 'environment');

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check for multiple cameras on mount
  useEffect(() => {
    const checkMultipleCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch {
        setHasMultipleCameras(false);
      }
    };
    checkMultipleCameras();
  }, []);

  const stopCamera = useCallback(() => {
    // Clear any pending timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
    }

    // Clear video source with delay for mobile
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Reset state
    streamRef.current = null;
    setStream(null);
    setIsInitializing(false);
    setFlashEnabled(false);
    setHasFlashSupport(false);
  }, []);

  const startCameraWithFacingMode = useCallback(
    async (mode: 'user' | 'environment') => {
      setIsInitializing(true);
      setError(null);

      try {
        // Add delay for mobile browsers
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: mode,
            width: config.width || { ideal: 1920 },
            height: config.height || { ideal: 1080 },
            torch: flashEnabled,
          },
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

        // Check if component is still mounted and refs are available
        if (!videoRef.current) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }

        // Set up video element
        videoRef.current.srcObject = mediaStream;
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setIsInitializing(false);

        // Check for flash support
        const videoTrack = mediaStream.getVideoTracks()[0];
        if (videoTrack) {
          const capabilities = videoTrack.getCapabilities?.();
          const supportsFlash = capabilities?.torch === true;
          setHasFlashSupport(supportsFlash);
        }
      } catch (err) {
        setIsInitializing(false);

        let errorMessage = 'Unable to access camera';
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError') {
            errorMessage = 'Camera permission denied. Please allow camera access and try again.';
          } else if (err.name === 'NotFoundError') {
            errorMessage = 'No camera found on this device.';
          } else if (err.name === 'NotReadableError') {
            errorMessage = 'Camera is already in use by another application.';
          } else {
            errorMessage = `Camera error: ${err.message}`;
          }
        }

        setError(errorMessage);
      }
    },
    [config.width, config.height, flashEnabled]
  );

  const startCamera = useCallback(async () => {
    // Prevent multiple simultaneous starts
    if (isInitializing || streamRef.current) {
      return;
    }

    // Stop any existing camera first
    stopCamera();

    await startCameraWithFacingMode(facingModeRef.current);
  }, [isInitializing, stopCamera, startCameraWithFacingMode]);

  const switchCamera = useCallback(async () => {
    // Stop current stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }

    // Toggle facing mode
    const newFacingMode = facingModeRef.current === 'environment' ? 'user' : 'environment';
    facingModeRef.current = newFacingMode;
    setFacingMode(newFacingMode);

    // Small delay for mobile browsers
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Restart with new facing mode
    await startCameraWithFacingMode(newFacingMode);
  }, [startCameraWithFacingMode]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !stream) {
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    // Check if video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return null;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Clear canvas first
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the current video frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);

    // Validate the data URL
    if (!imageDataUrl || imageDataUrl.length < 100 || !imageDataUrl.startsWith('data:image/')) {
      return null;
    }

    return imageDataUrl;
  }, [stream]);

  const toggleFlash = useCallback(async () => {
    if (!streamRef.current || !hasFlashSupport) {
      return;
    }

    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) {
      return;
    }

    try {
      const newFlashState = !flashEnabled;

      await videoTrack.applyConstraints({
        advanced: [{ torch: newFlashState }],
      });

      setFlashEnabled(newFlashState);
    } catch {
      // Try alternative approach for some browsers
      try {
        await videoTrack.applyConstraints({
          torch: !flashEnabled,
        });

        const newFlashState = !flashEnabled;
        setFlashEnabled(newFlashState);
      } catch {
        // Flash toggle not supported by this browser
      }
    }
  }, [flashEnabled, hasFlashSupport]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return {
    stream,
    error,
    isInitializing,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    capturePhoto,
    clearError,
    flashEnabled,
    hasFlashSupport,
    toggleFlash,
    facingMode,
    hasMultipleCameras,
    switchCamera,
  };
};
